import { Component } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { whitespaceValidator } from '@vendasta/forms';
import { PipelineSettingsService, StoredPercentageToDisplay } from './pipeline-settings.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { firstValueFrom, Observable } from 'rxjs';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { PipelineInterface } from '@vendasta/sales-opportunities';
import { OpenConfirmationModalService } from '@vendasta/galaxy/confirmation-modal';

@Component({
  selector: 'sales-ui-pipeline-settings',
  templateUrl: './pipeline-settings.component.html',
  styleUrls: ['./pipeline-settings.component.scss'],
})
export class PipelineSettingsComponent {
  protected readonly pipelineForm = new FormGroup({
    stages: new FormArray<FormGroup>([]),
    name: new FormControl('', whitespaceValidator()),
  });
  protected currentPipeline$ = new Observable<PipelineInterface>();

  constructor(
    private readonly pipelineSettingsService: PipelineSettingsService,
    private readonly snackbarService: SnackbarService,
    private readonly confirmationModal: OpenConfirmationModalService,
  ) {
    this.currentPipeline$ = this.pipelineSettingsService.currentPipeline$;
    this.currentPipeline$.pipe(takeUntilDestroyed()).subscribe((pipeline) => this.setFormValues(pipeline));
  }
  private setFormValues(pipeline: PipelineInterface): void {
    this.pipelineForm.controls.name.setValue(pipeline.name);
    this.stages.clear();
    pipeline.stages.forEach((stage) => {
      const newStage = new FormGroup({
        name: new FormControl(stage.name, whitespaceValidator()),
        probability: new FormControl(StoredPercentageToDisplay(stage.probability), [
          Validators.min(0),
          Validators.max(99),
        ]),
      });
      this.stages.push(newStage);
    });
  }
  protected get stages(): FormArray<FormGroup> {
    return this.pipelineForm.controls['stages'] as FormArray;
  }
  protected async createNewPipeline(): Promise<void> {
    const success = await firstValueFrom(this.pipelineSettingsService.createDefaultPipeline());
    if (success) {
      this.snackbarService.openSuccessSnack('FRONTEND.SALES_UI.PIPELINE.SETTINGS.SNACKBARS.CREATE_SUCCESS');
    } else {
      this.snackbarService.openErrorSnack('FRONTEND.SALES_UI.PIPELINE.SETTINGS.SNACKBARS.CREATE_FAILED');
    }
  }

  protected saveChanges(): void {
    //todo SNP-2633
  }

  protected cancelChanges(): void {
    //todo SNP-2633
  }

  protected async deletePipeline(pipelineId: string): Promise<void> {
    const proceed = await firstValueFrom(
      this.confirmationModal.openModal({
        type: 'warn',
        title: 'FRONTEND.SALES_UI.PIPELINE.SETTINGS.MODALS.DELETE_TITLE',
        message: 'FRONTEND.SALES_UI.PIPELINE.SETTINGS.MODALS.DELETE_MESSAGE',
        confirmButtonText: 'FRONTEND.SALES_UI.COMMON.ACTION_LABELS.DELETE',
        cancelButtonText: 'FRONTEND.SALES_UI.COMMON.ACTION_LABELS.CANCEL',
        actionOnEnterKey: false,
      }),
    );
    if (proceed) {
      const success = await firstValueFrom(this.pipelineSettingsService.deletePipeline(pipelineId));
      if (success) {
        this.snackbarService.openSuccessSnack('FRONTEND.SALES_UI.PIPELINE.SETTINGS.SNACKBARS.DELETE_SUCCESS');
      } else {
        this.snackbarService.openErrorSnack('FRONTEND.SALES_UI.PIPELINE.SETTINGS.SNACKBARS.DELETE_FAILED');
      }
    }
  }

  protected addStage(): void {
    const newStage = new FormGroup({
      name: new FormControl('', whitespaceValidator()),
      probability: new FormControl(0, [Validators.min(0), Validators.max(99)]),
    });
    this.stages.push(newStage);
  }

  protected deleteStage(stageIndex: number): void {
    this.stages.removeAt(stageIndex);
  }
}
